import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/api";

// ---------------------------------------------------------------------------
// Nomes dos atributos personalizados vindos do /wbs (linha por atributo)
// ---------------------------------------------------------------------------
const ATRIBUTOS = {
  DATA_INICIO: "Data de Início",
  DATA_FIM: "Data de fim",
  PROGRESSO: "Progresso(%)",
  TEMPO_ESPERADO: "Tempo esperado",
  TEMPO_GASTO: "Tempo gasto",
};

// ---------------------------------------------------------------------------
// Ícones inline (sem dependência externa)
// ---------------------------------------------------------------------------

const IconChevron = ({ open, size = 18, color = "#6b7280" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform .15s" }}
  >
    <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconDrag = ({ size = 14, color = "#9ca3af" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    {[6, 12, 18].map((cy) =>
      [8, 16].map((cx) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.6" />)
    )}
  </svg>
);

const IconUser = ({ size = 14, color = "#9ca3af" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4 4-6 8-6s8 2 8 6" strokeLinecap="round" />
  </svg>
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDate(value) {
  if (!value) return "--/--/--";
  const data = new Date(value);
  if (Number.isNaN(data.getTime())) return String(value);
  return data.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function isConcluida(status) {
  const normalizado = String(status || "").toLowerCase();
  return normalizado.includes("conclu") || normalizado.includes("closed") || normalizado.includes("done");
}

function isEmAndamento(status) {
  const normalizado = String(status || "").toLowerCase();
  return normalizado.includes("andamento") || normalizado.includes("progress");
}

function statusStyle(status) {
  if (isConcluida(status)) return { bg: "#dcfce7", color: "#166534" };
  if (isEmAndamento(status)) return { bg: "#ffedd5", color: "#c2410c" };
  return { bg: "#e5e7eb", color: "#374151" };
}

function progressoStyle(valor) {
  if (valor >= 100) return { bg: "#dcfce7", color: "#166534" };
  if (valor > 0) return { bg: "#dbeafe", color: "#1d4ed8" };
  return { bg: "#e5e7eb", color: "#374151" };
}

// Junta as linhas (1 por atributo) em um objeto por Historia de Usuário
function pivotHistorias(rows) {
  const mapa = new Map();
  rows.forEach((row) => {
    const id = row.id_historia_de_usuario;
    if (!mapa.has(id)) {
      mapa.set(id, {
        id,
        projetoId: row.id_projeto,
        responsavelId: row.id_responsavel,
        titulo: row.titulo_hu,
        status: row.status_hu,
        colaborador: row.nome_colaborador,
        roles: row.roles,
        atributos: {},
      });
    }
    if (row.nome_atributo) {
      mapa.get(id).atributos[row.nome_atributo] = row.valor_atributo;
    }
  });
  return Array.from(mapa.values());
}

// Agrupa as tarefas (HUs) por projeto -> cada projeto vira uma "Atividade"
function agruparPorAtividade(tarefas, nomesProjetos) {
  const grupos = new Map();
  tarefas.forEach((tarefa) => {
    const chave = tarefa.projetoId;
    if (!grupos.has(chave)) {
      grupos.set(chave, {
        id: chave,
        nome: nomesProjetos.get(chave) || `Projeto ${chave}`,
        tarefas: [],
      });
    }
    grupos.get(chave).tarefas.push(tarefa);
  });

  return Array.from(grupos.values()).map((grupo) => {
    let inicio = null;
    let fim = null;
    grupo.tarefas.forEach((t) => {
      const di = new Date(t.atributos[ATRIBUTOS.DATA_INICIO]);
      const df = new Date(t.atributos[ATRIBUTOS.DATA_FIM]);
      if (!Number.isNaN(di.getTime()) && (!inicio || di < inicio)) inicio = di;
      if (!Number.isNaN(df.getTime()) && (!fim || df > fim)) fim = df;
    });

    const todasConcluidas = grupo.tarefas.length > 0 && grupo.tarefas.every((t) => isConcluida(t.status));
    const algumaEmAndamento = grupo.tarefas.some((t) => isEmAndamento(t.status) || isConcluida(t.status));
    const statusDerivado = todasConcluidas ? "Concluída" : algumaEmAndamento ? "Em andamento" : "Pendente";

    return { ...grupo, inicio, fim, status: statusDerivado };
  });
}

function calcularProgresso(tarefa) {
  const progressoCustomizado = Number(
    tarefa.atributos[ATRIBUTOS.PROGRESSO]
  );

  if (!Number.isNaN(progressoCustomizado)) {
    return Math.min(Math.max(progressoCustomizado, 0), 100);
  }

  const tempoEsperado = Number(
    tarefa.atributos[ATRIBUTOS.TEMPO_ESPERADO]
  );

  const tempoGasto = Number(
    tarefa.atributos[ATRIBUTOS.TEMPO_GASTO]
  );

  if (
    !Number.isNaN(tempoEsperado) &&
    !Number.isNaN(tempoGasto) &&
    tempoEsperado > 0
  ) {
    return Math.min(
      Math.max((tempoGasto / tempoEsperado) * 100, 0),
      100,
    );
  }

  return 0;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------
export default function Wbs() {
  const [projetos, setProjetos] = useState([]);
  const [projetoSelecionado, setProjetoSelecionado] = useState("");
  const [linhas, setLinhas] = useState([]);
  const [loadingProjetos, setLoadingProjetos] = useState(false);
  const [loadingWbs, setLoadingWbs] = useState(false);
  const [colapsados, setColapsados] = useState(new Set());
  const [concluidasLocais, setConcluidasLocais] = useState(new Set());

  useEffect(() => {
    const carregarProjetos = async () => {
      setLoadingProjetos(true);
      try {
        const response = await api.get("/api/planejamentoProjetos");
        setProjetos(response.data || []);
      } catch (error) {
        console.error("Erro ao buscar projetos:", error);
      } finally {
        setLoadingProjetos(false);
      }
    };
    carregarProjetos();
  }, []);

  useEffect(() => {
    const carregarWbs = async () => {
      setLoadingWbs(true);
      try {
        const requestConfig = projetoSelecionado ? { params: { projectId: projetoSelecionado } } : {};
        const response = await api.get("/wbs", requestConfig);
        setLinhas(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Erro ao buscar WBS:", error);
        setLinhas([]);
      } finally {
        setLoadingWbs(false);
      }
    };

    carregarWbs();
  }, [projetoSelecionado]);

  const nomesProjetos = useMemo(() => {
    const mapa = new Map();
    projetos.forEach((p) => mapa.set(p.id, p.nome));
    return mapa;
  }, [projetos]);

  const tarefas = useMemo(() => pivotHistorias(linhas), [linhas]);

  const atividades = useMemo(() => agruparPorAtividade(tarefas, nomesProjetos), [tarefas, nomesProjetos]);

  // Expande automaticamente as novas atividades carregadas
  useEffect(() => {
    setColapsados((prev) => {
      const atualId = new Set(atividades.map((a) => a.id));
      const novo = new Set();
      prev.forEach((id) => {
        if (atualId.has(id)) novo.add(id);
      });
      return novo;
    });
  }, [atividades]);

  const stats = useMemo(() => {
    let somaProgresso = 0;
    let qtdProgresso = 0;
    let somaEsperado = 0;
    let somaGasto = 0;
    let terminoDesejado = null;

    tarefas.forEach((t) => {
      const progresso = calcularProgresso(t);
      if (!Number.isNaN(progresso)) {
        somaProgresso += progresso;
        qtdProgresso += 1;
      }
      const esperado = Number(t.atributos[ATRIBUTOS.TEMPO_ESPERADO]);
      if (!Number.isNaN(esperado)) somaEsperado += esperado;

      const gasto = Number(t.atributos[ATRIBUTOS.TEMPO_GASTO]);
      if (!Number.isNaN(gasto)) somaGasto += gasto;

      const df = new Date(t.atributos[ATRIBUTOS.DATA_FIM]);
      if (!Number.isNaN(df.getTime()) && (!terminoDesejado || df > terminoDesejado)) {
        terminoDesejado = df;
      }
    });

    const concluidas = tarefas.filter(
      (t) => isConcluida(t.status) || concluidasLocais.has(t.id)
    ).length;

    return {
      totalAtividades: atividades.length,
      totalTarefas: tarefas.length,
      concluidas,
      progressoGeral: qtdProgresso > 0 ? Math.round(somaProgresso / qtdProgresso) : 0,
      somaEsperado: Math.round(somaEsperado * 10) / 10,
      somaGasto: Math.round(somaGasto * 10) / 10,
      percentualHoras: somaEsperado > 0 ? Math.round((somaGasto / somaEsperado) * 100) : 0,
      terminoDesejado,
    };
  }, [tarefas, atividades, concluidasLocais]);

  const toggleColapso = (id) => {
    setColapsados((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };

  const toggleConcluida = (id) => {
    setConcluidasLocais((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  };

  const carregando = loadingProjetos || loadingWbs;

  return (
    <div style={{ background: "#f3f4f6", minHeight: "100vh", padding: "32px 0 60px" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 20px" }}>
        <div
          style={{
            fontSize: "12px",
            color: "#6b7280",
            marginBottom: "18px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>Home</span>
          <span>›</span>
          <span>Gestão</span>
          <span>›</span>
          <strong style={{ color: "#374151" }}>Planejamento de Projeto</strong>
        </div>

        <h1 style={{ fontSize: "44px", margin: 0, fontWeight: 700, color: "#1f2937" }}>
          Planejamento de Projeto
        </h1>
        <p style={{ margin: "8px 0 20px", color: "#4b5563", fontSize: "18px" }}>
          Acompanhamento de atividades e tarefas do projeto
        </p>

        {/* Seletor de projeto */}
        <div style={{ borderTop: "1px dashed #d1d5db", marginBottom: "24px", paddingTop: "18px" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: 600, color: "#374151" }}>
              Selecione projeto
            </label>
            <select
              value={projetoSelecionado}
              onChange={(event) => setProjetoSelecionado(event.target.value)}
              disabled={loadingProjetos}
              style={{
                width: "100%",
                maxWidth: "500px",
                height: "42px",
                padding: "8px 12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                background: "#fff",
                fontSize: "16px",
                color: "#111827",
              }}
            >
              <option value="">{loadingProjetos ? "Carregando..." : "Todos os projetos"}</option>
              {projetos.map((projeto) => (
                <option key={projeto.id} value={projeto.id}>
                  {projeto.nome}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Header de progresso geral */}
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #dfe3e8",
            borderRadius: "10px",
            padding: "18px 20px",
            marginBottom: "20px",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div style={{ color: "#374151", fontSize: "16px" }}>
              Progresso Geral <strong style={{ color: "#111827" }}>{stats.progressoGeral}%</strong>
            </div>
            <div style={{ fontSize: "13px", color: "#6b7280" }}>
              Término desejado:{" "}
              <strong style={{ color: "#374151" }}>
                {stats.terminoDesejado ? formatDate(stats.terminoDesejado) : "--/--/----"}
              </strong>
            </div>
          </div>

          <div
            style={{
              marginTop: "10px",
              width: "100%",
              height: "8px",
              borderRadius: "999px",
              background: "#dfe3e8",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${stats.progressoGeral}%`,
                height: "100%",
                background: "#111827",
                borderRadius: "999px",
              }}
            />
          </div>

          <div style={{ marginTop: "10px", fontSize: "13px", color: "#6b7280" }}>
            {stats.totalAtividades} atividades &nbsp;•&nbsp; {stats.totalTarefas} tarefas &nbsp;•&nbsp;{" "}
            {stats.concluidas} concluídas &nbsp;•&nbsp; consumo de horas:{" "}
            <strong style={{ color: "#c2410c" }}>
              {stats.somaGasto}h / {stats.somaEsperado}h
            </strong>{" "}
            ({stats.percentualHoras}%)
          </div>
        </div>

        {/* Conteúdo WBS */}
        {carregando ? (
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #dfe3e8",
              borderRadius: "10px",
              padding: "24px",
              color: "#6b7280",
              textAlign: "center",
            }}
          >
            Carregando...
          </div>
        ) : atividades.length === 0 ? (
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #dfe3e8",
              borderRadius: "10px",
              padding: "24px",
              color: "#6b7280",
              textAlign: "center",
            }}
          >
            Nenhuma atividade encontrada.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {atividades.map((atividade) => {
              const aberta = !colapsados.has(atividade.id);
              const badge = statusStyle(atividade.status);

              return (
                <div
                  key={atividade.id}
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #dfe3e8",
                    borderRadius: "10px",
                    overflow: "hidden",
                  }}
                >
                  {/* Cabeçalho da atividade */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "14px 16px",
                      background: "#fff",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <IconDrag />
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <strong style={{ color: "#111827", fontSize: "15px" }}>{atividade.nome}</strong>
                          <span
                            style={{
                              background: badge.bg,
                              color: badge.color,
                              fontSize: "12px",
                              fontWeight: 600,
                              padding: "2px 10px",
                              borderRadius: "999px",
                            }}
                          >
                            {atividade.status}
                          </span>
                        </div>
                        <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>
                          {formatDate(atividade.inicio)} — {formatDate(atividade.fim)} &nbsp;•&nbsp;{" "}
                          {atividade.tarefas.length} tarefas
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <button
                        onClick={() => toggleColapso(atividade.id)}
                        style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}
                      >
                        <IconChevron open={aberta} />
                      </button>
                    </div>
                  </div>

                  {/* Tarefas */}
                  {aberta && (
                    <div>
                      {atividade.tarefas.map((tarefa) => {
                        const statusBadge = statusStyle(tarefa.status);
                        const progresso = Math.round(calcularProgresso(tarefa));
                        const progBadge = progressoStyle(progresso);
                        const concluida = isConcluida(tarefa.status) || concluidasLocais.has(tarefa.id);

                        return (
                          <div
                            key={tarefa.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "12px 16px 12px 40px",
                              borderTop: "1px solid #e5e7eb",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <input
                                type="checkbox"
                                checked={concluida}
                                onChange={() => toggleConcluida(tarefa.id)}
                              />
                              <div>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                  <span style={{ color: "#111827", fontWeight: 500 }}>{tarefa.titulo}</span>
                                  <span
                                    style={{
                                      background: statusBadge.bg,
                                      color: statusBadge.color,
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      padding: "2px 8px",
                                      borderRadius: "999px",
                                    }}
                                  >
                                    {tarefa.status}
                                  </span>
                                  <span
                                    style={{
                                      background: progBadge.bg,
                                      color: progBadge.color,
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      padding: "2px 8px",
                                      borderRadius: "999px",
                                    }}
                                  >
                                    {progresso}%
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    fontSize: "13px",
                                    color: "#6b7280",
                                    marginTop: "2px",
                                  }}
                                >
                                  <IconUser />
                                  {tarefa.colaborador || "Sem responsável"} &nbsp;•&nbsp;{" "}
                                  {formatDate(tarefa.atributos[ATRIBUTOS.DATA_INICIO])} —{" "}
                                  {formatDate(tarefa.atributos[ATRIBUTOS.DATA_FIM])}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}